import { useEffect, useRef, useState } from "react";
import { C } from "../ui";

// Wraps a horizontally-scrolling row (app-tabbar, trade-scroll) and
// overlays a "More ›" chevron on the trailing edge whenever the row's
// content actually extends past what's visible. The CSS scroll-shadow
// alone (mobile.css) wasn't a strong enough cue on its own — this is an
// explicit, clickable affordance on top of it. A 1px sentinel appended
// after the row's real content is watched via IntersectionObserver: not
// visible means there's more to scroll to, so show the chevron; it
// disappears the moment the user actually scrolls (or taps) to the true
// end. `fadeColor` must match whatever's behind the row (its own
// background, not the page's) for the fade-out gradient to blend in.
export default function ScrollHint({ className, style, outerStyle, fadeColor = C.white, children }) {
  const scrollRef = useRef(null);
  const sentinelRef = useRef(null);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = scrollRef.current;
    if (!sentinel || !root) return;
    const obs = new IntersectionObserver(([entry]) => setHasMore(!entry.isIntersecting), { root, threshold: 1 });
    obs.observe(sentinel);
    return () => obs.disconnect();
  });

  return (
    <div style={{ position: "relative", ...outerStyle }}>
      <div className={className} style={style} ref={scrollRef}>
        {children}
        <div ref={sentinelRef} style={{ width: 1, flexShrink: 0 }} />
      </div>
      {hasMore && (
        <button
          onClick={() => scrollRef.current?.scrollBy({ left: 160, behavior: "smooth" })}
          style={{
            position: "absolute", right: 0, top: 0, bottom: 0,
            display: "flex", alignItems: "center", gap: 2,
            padding: "0 10px 0 22px", border: "none", cursor: "pointer",
            fontSize: 12, fontWeight: 700, color: C.accent, whiteSpace: "nowrap",
            background: `linear-gradient(to right, transparent, ${fadeColor} 45%)`,
          }}
        >
          More ›
        </button>
      )}
    </div>
  );
}
